import React from 'react';

import { Box } from '@mui/joy';
import { useMediaQuery } from '@mui/material';

import Arrow from '../../assets/images/Arrow.svg?react';
import { UnitConversions, normalizeDegrees, roundFloatToInt } from '../../general-utilities/numbers-util';
import { InstrumentSettings } from './CrewInstrumentSettingsModal';
import AttitudeIndicator from './Indicators/Attitude';
import Battery from './Indicators/Battery';
import Compass from './Indicators/Compass';
import Thurst from './Indicators/Thrust';
import { TelemetryData } from './types';

interface InstrumentsPanelProps {
  isInstrumentsVisible: boolean;
  instrumentSettings: InstrumentSettings;
  latestCrewTelemetry: TelemetryData | null;
  onToggleVisibility: () => void;
  onOpenSettings: () => void;
}

const InstrumentsPanel: React.FC<InstrumentsPanelProps> = React.memo(
  ({ isInstrumentsVisible, instrumentSettings, latestCrewTelemetry, onToggleVisibility, onOpenSettings }) => {
    // Use useMediaQuery with noSsr: true to ensure it's reactive on client side
    // The matchMedia API will automatically update when window size/orientation changes
    const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-width: 1024px)', {
      noSsr: true,
      defaultMatches: false
    });

    const hasAnyInstrumentEnabled =
      instrumentSettings.attitudeIndicator ||
      instrumentSettings.compass ||
      instrumentSettings.thrust ||
      instrumentSettings.battery;

    // Size for mobile landscape: 85px to fit 4 instruments in 400px width

    return (
      <Box
        className='absolute w-fit right-0 top-[7%] p-0.5 flex flex-col sm:flex-row flex-wrap gap-2 items-center justify-center z-10 sm:h-[119px]'
        sx={{
          backgroundColor: 'color-mix(in srgb, var(--joy-palette-background-level1) 60%, transparent)',
          borderRadius: '60px',
          opacity: 1,
          // Mobile landscape: fit content with max 400px width, 99px height
          '@media (orientation: landscape) and (max-width: 1024px)': {
            width: 'fit-content',
            height: '99px',
            maxWidth: '400px',
            maxHeight: '99px',
            top: '40px', // Position below header (header is min-h-[40px] on mobile)
            flexWrap: 'nowrap', // Ensure all instruments stay in one row
            overflow: 'visible', // Ensure all instruments are visible
            padding: '1px', // Minimal padding for rounded corners
            margin: '0px' // No margin
          }
        }}
        onClick={onOpenSettings}
      >
        <Box
          className='cursor-pointer z-20'
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (hasAnyInstrumentEnabled) {
              onToggleVisibility();
            } else {
              onOpenSettings();
            }
          }}
        >
          <Box className='hidden sm:block'>
            <Arrow
              className='w-4 h-4'
              style={{ rotate: isInstrumentsVisible ? '270deg' : '90deg' }}
              fill='var(--joy-palette-common-offWhite)'
            />
          </Box>
          <Box className='sm:hidden'>
            <Arrow
              className='w-4 h-4'
              style={{ rotate: isInstrumentsVisible ? '180deg' : '0deg' }}
              fill='var(--joy-palette-common-offWhite)'
            />
          </Box>
        </Box>
        {isInstrumentsVisible && (
          <>
            {instrumentSettings.attitudeIndicator && (
              <Box
                className={isMobileLandscape ? '' : 'sm:min-w-[111px] sm:min-h-[111px] w-[90px] h-[90px]'}
                sx={{
                  ...(isMobileLandscape && {
                    width: '85px',
                    height: '85px',
                    minWidth: '85px',
                    minHeight: '85px',
                    maxWidth: '85px',
                    maxHeight: '85px',
                    flexShrink: 0,
                    // Scale Typography proportionally: 85/111 ≈ 0.766
                    fontSize: '76.6%',
                    '& .MuiTypography-root': {
                      fontSize: 'inherit'
                    }
                  })
                }}
              >
                <AttitudeIndicator
                  roll={roundFloatToInt(latestCrewTelemetry?.attitude_euler?.roll_deg)}
                  pitch={Number(
                    roundFloatToInt(latestCrewTelemetry?.attitude_euler?.pitch_deg) +
                      roundFloatToInt(latestCrewTelemetry?.attitude_euler?.pitch_offset_deg)
                  )}
                />
              </Box>
            )}
            {instrumentSettings.compass && (
              <Box
                className={isMobileLandscape ? '' : 'sm:min-w-[111px] sm:min-h-[111px] w-[90px] h-[90px]'}
                sx={{
                  ...(isMobileLandscape && {
                    width: '85px',
                    height: '85px',
                    minWidth: '85px',
                    minHeight: '85px',
                    maxWidth: '85px',
                    maxHeight: '85px',
                    flexShrink: 0,
                    position: 'relative',
                    overflow: 'hidden'
                  })
                }}
              >
                <Box
                  sx={{
                    ...(isMobileLandscape && {
                      width: '111px',
                      height: '111px',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) scale(0.766)',
                      transformOrigin: 'center center'
                    })
                  }}
                >
                  <Compass
                    windSpeed={UnitConversions.mpsToMph(latestCrewTelemetry?.wind?.speed_m_s ?? 0)}
                    windDirection={normalizeDegrees(roundFloatToInt(latestCrewTelemetry?.wind?.direction_deg))}
                    rotation={normalizeDegrees(Math.trunc(latestCrewTelemetry?.attitude_euler?.yaw_deg ?? 0))}
                    isMobileLandscape={isMobileLandscape}
                  />
                </Box>
              </Box>
            )}
            {instrumentSettings.thrust && (
              <Box
                className={isMobileLandscape ? '' : 'sm:min-w-[111px] sm:min-h-[111px] w-[90px] h-[90px]'}
                sx={{
                  ...(isMobileLandscape && {
                    width: '85px',
                    height: '85px',
                    minWidth: '85px',
                    minHeight: '85px',
                    maxWidth: '85px',
                    maxHeight: '85px',
                    flexShrink: 0,
                    // Scale Typography proportionally: 85/111 ≈ 0.766
                    fontSize: '76.6%',
                    '& .MuiTypography-root': {
                      fontSize: 'inherit'
                    }
                    // Note: Ellipses use percentage-based sizing (w-[50%] h-[50%]) so they scale naturally
                    // ThrustIcon will also scale naturally with the container
                  })
                }}
              >
                <Thurst
                  valueTl={
                    latestCrewTelemetry?.esc_status?.max_rpm && latestCrewTelemetry?.esc_status?.thrust_front_left_rpm
                      ? roundFloatToInt(
                          100 *
                            (latestCrewTelemetry.esc_status.thrust_front_left_rpm /
                              latestCrewTelemetry.esc_status.max_rpm)
                        )
                      : 0
                  }
                  valueTr={
                    latestCrewTelemetry?.esc_status?.max_rpm && latestCrewTelemetry?.esc_status?.thrust_front_right_rpm
                      ? roundFloatToInt(
                          100 *
                            (latestCrewTelemetry.esc_status.thrust_front_right_rpm /
                              latestCrewTelemetry.esc_status.max_rpm)
                        )
                      : 0
                  }
                  valueBl={
                    latestCrewTelemetry?.esc_status?.max_rpm && latestCrewTelemetry?.esc_status?.thrust_rear_left_rpm
                      ? roundFloatToInt(
                          100 *
                            (latestCrewTelemetry.esc_status.thrust_rear_left_rpm /
                              latestCrewTelemetry.esc_status.max_rpm)
                        )
                      : 0
                  }
                  valueBr={
                    latestCrewTelemetry?.esc_status?.max_rpm && latestCrewTelemetry?.esc_status?.thrust_rear_right_rpm
                      ? roundFloatToInt(
                          100 *
                            (latestCrewTelemetry.esc_status.thrust_rear_right_rpm /
                              latestCrewTelemetry.esc_status.max_rpm)
                        )
                      : 0
                  }
                />
              </Box>
            )}
            {instrumentSettings.battery && (
              <Box
                className={isMobileLandscape ? '' : 'sm:min-w-[111px] sm:min-h-[111px] w-[90px] h-[90px]'}
                sx={{
                  ...(isMobileLandscape && {
                    width: '85px',
                    height: '85px',
                    minWidth: '85px',
                    minHeight: '85px',
                    maxWidth: '85px',
                    maxHeight: '85px',
                    flexShrink: 0,
                    position: 'relative',
                    overflow: 'hidden'
                  })
                }}
              >
                <Box
                  sx={{
                    ...(isMobileLandscape && {
                      width: '111px',
                      height: '111px',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) scale(0.766)',
                      transformOrigin: 'center center'
                    })
                  }}
                >
                  <Battery
                    value={(latestCrewTelemetry?.battery?.remaining_percent ?? 0) / 100}
                    time_remaining_s={latestCrewTelemetry?.battery?.time_remaining_s ?? 0}
                    time_to_home_s={latestCrewTelemetry?.battery?.time_to_home_s ?? 0}
                    time_to_land_s={latestCrewTelemetry?.battery?.time_to_land_s ?? 0}
                    droneStatus={latestCrewTelemetry?.status?.landed_state ?? ''}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }
);

InstrumentsPanel.displayName = 'InstrumentsPanel';

export default InstrumentsPanel;
